import { TFunction } from 'i18next';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.js';

import { CommitmentCard } from '../../molecules/commitment-card/commitment-card.tsx';
import { SectionCardList } from '../section-card-list/section-card-list.js';
import {
  GivingItemWithCommitment,
  GroupedGivingHistoryItems,
  formatGivingItemsDateGroup,
  formatNoCommitmentData,
  formatOverrideData,
  getGivingHistoryTotal,
  getSortCommitmentsByTitle,
  groupGivingHistoryByType,
  groupGivingItemsByDate,
} from './utils';

export type GivingHistoryMonthProps =
  | {
      isLoading: true;
      heading?: never;
      givingHistoryItems?: never;
    }
  | {
      isLoading?: false;
      heading: string;
      givingHistoryItems: GivingItemWithCommitment[];
    };

export const useComponentData = ({
  givingHistoryItems,
  t,
}: {
  givingHistoryItems: GivingHistoryMonthProps['givingHistoryItems'];
  t: TFunction<string, string>;
}) => {
  const givingItemsGroupedByDate = givingHistoryItems?.length
    ? groupGivingItemsByDate(givingHistoryItems)
    : null;

  const sorted: GroupedGivingHistoryItems = !givingItemsGroupedByDate
    ? {}
    : Object.entries(
        givingItemsGroupedByDate,
      ).reduce<GroupedGivingHistoryItems>((acc, [key, givingHistoryList]) => {
        const grouped = groupGivingHistoryByType(givingHistoryList);

        const {
          [CommitmentTypeEnum.ChildSponsorship]: childSponsorships,
          [CommitmentTypeEnum.GiftCommitment]: giftCommitments,
          ...remainingCommitmentTypes
        } = grouped;

        const remainingCommitments = Object.keys(remainingCommitmentTypes)
          .map((commitmentType) => remainingCommitmentTypes[commitmentType])
          .flat();

        acc[key] = [
          ...childSponsorships.toSorted(getSortCommitmentsByTitle(t)),
          ...giftCommitments.toSorted(getSortCommitmentsByTitle(t)),
          ...remainingCommitments.toSorted(getSortCommitmentsByTitle(t)),
        ];

        return acc;
      }, {});

  return {
    givingItemDateGroupsFormatted: sorted
      ? formatGivingItemsDateGroup(sorted).toReversed()
      : null,
  };
};

/**
 * Renders group of giving history cards belonging to the same momth.
 *
 * Seemsed that memoizing the entire component was best since it never needs to rerender
 * and has some expensive calculations based on an array that isn't guaranteed to be memoized.
 */
export const GivingHistoryMonth: React.FC<GivingHistoryMonthProps> = memo(
  ({ heading, givingHistoryItems, isLoading }) => {
    const { t } = useTranslation(['my', 'common']);
    const { givingItemDateGroupsFormatted } = useComponentData({
      givingHistoryItems,
      t,
    });

    return (
      <div
        className="py-comfy-xl"
        style={{
          borderTop: '1px solid var(--color-container-border-neutral-mild)',
        }}
      >
        {isLoading && <SectionCardList isLoadingCards isLoadingHeading />}

        {!isLoading && (
          <SectionCardList
            heading={heading}
            subheading={t('common:numbers.currency', {
              amount: `${getGivingHistoryTotal(givingHistoryItems)}`,
            })}
          >
            <div className="flex flex-col gap-comfy-xl">
              {givingItemDateGroupsFormatted?.map(
                ({
                  heading: subHeading,
                  givingHistoryItems: givingHistoryItemsByDate,
                }) => {
                  return (
                    <div
                      key={`giving-history-date-group-${subHeading}`}
                      className="flex flex-col gap-comfy-sm"
                    >
                      <h3 className="label-potent label-md md:label-potent md:label-lg label-neutral-mild">
                        {subHeading}
                      </h3>

                      <div className="flex flex-col gap-snug-xl">
                        {givingHistoryItemsByDate.map((givingItem, index) => {
                          const commitment = givingItem.commitment!;
                          const overrideData = {
                            ...(!givingItem.commitment && {
                              ...formatNoCommitmentData(t, givingItem),
                            }),
                            rate: givingItem.isRefund
                              ? givingItem.amount * -1
                              : givingItem.amount,
                            ...(givingItem.stockGiftInKindDescription && {
                              ...formatOverrideData(t, givingItem),
                            }),
                          };

                          return (
                            <CommitmentCard
                              key={
                                /**
                                 * Using index here for cases when there are multiple payments for commitment during a single day.
                                 * Since these cards are never filtered/reordered using index shouldn't hurt anything.
                                 */
                                givingItem.commitmentId +
                                givingItem.recordDate +
                                index
                              }
                              commitment={commitment}
                              overrideData={overrideData}
                              variant="compact"
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </SectionCardList>
        )}
      </div>
    );
  },
  (prev, next) => prev.isLoading === next.isLoading,
);
